import { EditOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, Collapse, DatePicker, Form, Input, message, Modal, Row, Select, Table } from "antd";
import { getData, postData, putData } from "apiServices/common";
import AppPageHeader from "common/PageHeader";
import CompactHorizontalScroll from "common/CompactHorizontalScroll";
import ListTable from "common/ListTable";
import moment from "moment";
import React, { useEffect, useState } from "react";

const getDefaultDateRange = () => ({
  start_date: moment(),
  end_date: moment().add(3, "months"),
});

const PPAnalysisReport = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({});
  const [otherData, setOtherData] = useState({});
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [noteModalData, setNoteModalData] = useState({});
  const [noteModalLoading, setNoteModalLoading] = useState(false);
  const [buyers, setBuyers] = useState([]);
  const [buyingOffices, setBuyingOffices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [subContractFactories, setSubContractFactories] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  const fetchBuyers = async (filterValues) => {
    try {
      const bodyData = {
        per_page: filterValues?.per_page || 20,
        ...filterValues,
      };
      const response = await getData("/api/buyers", false, bodyData);
      if (response && response?.data?.code === 200) {
        setBuyers(response?.data?.data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching buyers:", error);
    }
  };

  const fetchBuyingOffices = async (filterValues) => {
    try {
      const bodyData = {
        per_page: filterValues?.per_page || 20,
        ...filterValues,
      };
      const response = await getData("/api/buying_office", false, bodyData);
      if (response && response?.data?.code === 200) {
        setBuyingOffices(response?.data?.data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching buying offices:", error);
    }
  };

  const fetchCountries = async (filterValues) => {
    try {
      const bodyData = {
        per_page: filterValues?.per_page || 20,
        status: "active",
        ...filterValues,
      };
      const response = await getData("/api/buyer-inquiries/get-countries", false, bodyData);
      if (response && response?.data?.code === 200) {
        setCountries(response?.data?.data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchSubContractFactories = async (filterValues) => {
    try {
      const bodyData = {
        per_page: filterValues?.per_page || 20,
        ...filterValues,
      };
      const response = await getData("/api/sub-contract-factory", false, bodyData);
      if (response && response?.data?.code === 200) {
        setSubContractFactories(response?.data?.data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching sub contract factories:", error);
    }
  };

  useEffect(() => {
    fetchBuyers();
    fetchBuyingOffices();
    fetchCountries();
    fetchSubContractFactories();
  }, []);

  const onFinish = async (values) => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = {
        start_date: values.start_date ? moment(values.start_date).format("YYYY-MM-DD") : null,
        end_date: values.end_date ? moment(values.end_date).format("YYYY-MM-DD") : null,
        buyer_id: values.buyer_id || null,
        buying_office_id: values.buying_office_id || null,
        country_id: values.country_id || null,
        sub_contract_factory_id: values.sub_contract_factory_id || null,
      };

      const response = await getData("/api/report/pp_analysis_preview", null, params);

      if (response?.data?.data?.details) {
        setReportData(response?.data?.data?.details);
        setOtherData(response?.data?.data?.others_data);
      } else {
        message.error("No data found");
        setReportData({});
        setOtherData({});
        setFetchError("No data found");
      }
    } catch (error) {
      console.error("Error fetching PP Analysis Report:", error);
      message.error("Failed to fetch report data");
      setReportData({});
      setOtherData({});
      setFetchError("Failed to fetch report data");
    } finally {
      setLoading(false);
    }
  };

  const getMonthKeys = () => {
    if (!reportData || Object.keys(reportData).length === 0) return [];
    return Object.keys(reportData).filter((key) => moment(key, "YYYY-MM", true).isValid());
  };

  const getRowKey = (item) => `${item?.buyer_name || ""}__${item?.buying_office_name || ""}`;

  const toNumber = (v) => {
    if (v === "" || v == null) return 0;
    const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const formatQty = (value) => toNumber(value).toLocaleString();

  const buildColumns = () => {
    if (!reportData || Object.keys(reportData).length === 0) {
      return [];
    }

    const columns = [];
    const monthKeys = getMonthKeys();

    // 1. Dynamic Monthly Columns - First 3 months: 4 cols, Rest: 6 cols
    const numRender = (value, record) =>
      record?.rowType === "black" || record?.rowType === "others" ? null : formatQty(value);
    const textRender = (value, record) =>
      record?.rowType === "black" || record?.rowType === "others" ? null : value || "-";

    const getHeaderRowStyle = (bgColor) => () => ({
      style: { backgroundColor: bgColor, borderColor: "rgba(255,255,255,0.1)" },
    });

    columns.push({
      title: "Customer Info",
      onHeaderCell: getHeaderRowStyle("#001529"), // Darkest blue for buyer info
      children: [
        {
          title: "Lead",
          dataIndex: "lead",
          key: "lead",
          width: 72,
          fixed: "left",
          render: (value, record) => {
            if (record?.rowType === "black" || record?.rowType === "others") return null;

            return <span className="cell-ellipsis" title={value}>{value}</span>;
          },
        },
        {
          title: "Merchant",
          dataIndex: "mercent",
          key: "mercent",
          width: 110,
          fixed: "left",
          render: (value, record) => {
            if (record?.rowType === "black" || record?.rowType === "others") return null;

            return <span className="cell-ellipsis" title={value}>{value}</span>;
          },
        },
        {
          title: "Country",
          dataIndex: "country",
          key: "country",
          width: 72,
          fixed: "left",
          render: textRender,
        },
        {
          title: "Buying Office",
          dataIndex: "buying_office_name",
          key: "buying_office_name",
          width: 120,
          fixed: "left",
          render: (value, record) => {
            if (record?.rowType === "black" || record?.rowType === "others") return null;
            return <span className="cell-ellipsis" title={value}>{value ?? ""}</span>;
          },
        },
        {
          title: "Buyer",
          dataIndex: "buyer_name",
          key: "buyer_name",
          width: 120,
          fixed: "left",
          render: (value, record) => {
            if (record?.rowType === "black" || record?.rowType === "others") return null;
            return <span className="cell-ellipsis" title={value}>{value ?? ""}</span>;
          },
        },
      ],
    });

    const monthBgColors = ["#1890ff", "#2db7f5", "#0050b3", "#096dd9", "#1d39c4", "#531dab"]; // Professional blue/purple theme palette

    monthKeys.forEach((date, monthIdx) => {
      const dateLabel = moment(date, "YYYY-MM").format("MMM'YY");
      const isFirstThreeMonths = monthIdx < 3;
      const monthColor = monthBgColors[monthIdx % monthBgColors.length];
      const baseChildren = [
        {
          title: isFirstThreeMonths ? "Conf. Plan" : "Conf. Qty",
          dataIndex: `${date}_confirmed_qty`,
          key: `${date}_confirmed_qty`,
          align: "right",
          width: 82,
          render: numRender,
        },
        {
          title: isFirstThreeMonths ? "Proj. Plan" : "Proj. Qty",
          dataIndex: `${date}_projection_qty`,
          key: `${date}_projection_qty`,
          align: "right",
          width: 82,
          render: numRender,
        },
        {
          title: "Book Qty",
          dataIndex: `${date}_boking_qty`,
          key: `${date}_boking_qty`,
          align: "right",
          width: 82,
          render: numRender,
        },
      ];
      if (isFirstThreeMonths) {
        baseChildren.push({
          title: "Note",
          dataIndex: `${date}_note`,
          key: `${date}_note`,
          width: 90,
          onCell: (record) => {
            const hasQty =
              Number(record[`${date}_confirmed_qty`]) > 0 ||
              Number(record[`${date}_projection_qty`]) > 0 ||
              Number(record[`${date}_boking_qty`]) > 0;
            if (hasQty && record?.rowType !== "black" && record?.rowType !== "others" && record?.rowType !== "total") {
              return { style: { backgroundColor: "#fffbe6" } };
            }
            return {};
          },
          render: (value, record) => {
            if (record?.rowType === "black" || record?.rowType === "others" || record?.rowType === "total")
              return value ?? "";
            const hasQty =
              Number(record[`${date}_confirmed_qty`]) > 0 ||
              Number(record[`${date}_projection_qty`]) > 0 ||
              Number(record[`${date}_boking_qty`]) > 0;
            if (!hasQty) return "-";
            return (
              <span
                style={{
                  cursor: "pointer",
                  color: "#1890ff",
                  display: "block",
                  width: "100%",
                  height: "100%",
                }}
                onClick={() => {
                  setNoteModalData({
                    buyer_id: record[`${date}_buyer_id`] || record.buyer_id || null,
                    sub_contract_id: record[`${date}_sub_contract_id`] || record.sub_contract_id || null,
                    year_month: date,
                    notes: value || "",
                    note_for: "monthly",
                    note_id: record[`${date}_note_id`],
                  });
                  setNoteModalVisible(true);
                }}
              >
                {value && value.length > 14 ? value.substring(0, 14) + "..." : value || ""}{" "}
                <EditOutlined style={{ fontSize: 12 }} />
              </span>
            );
          },
        });
      } else {
        baseChildren.push(
          {
            title: "Book Date",
            dataIndex: `${date}_boking_date`,
            key: `${date}_boking_date`,
            width: 90,
            render: textRender,
          },
          {
            title: "Will Conf.",
            dataIndex: `${date}_will_conf_capacity`,
            key: `${date}_will_conf_capacity`,
            width: 90,
            render: textRender,
          },
          {
            title: "Note",
            dataIndex: `${date}_note`,
            key: `${date}_note`,
            width: 90,
            onCell: (record) => {
              const hasQty =
                Number(record[`${date}_confirmed_qty`]) > 0 ||
                Number(record[`${date}_projection_qty`]) > 0 ||
                Number(record[`${date}_boking_qty`]) > 0;
              if (
                hasQty &&
                record?.rowType !== "black" &&
                record?.rowType !== "others" &&
                record?.rowType !== "total"
              ) {
                return { style: { backgroundColor: "#fffbe6" } };
              }
              return {};
            },
            render: (value, record) => {
              if (record?.rowType === "black" || record?.rowType === "others" || record?.rowType === "total")
                return value ?? "";
              const hasQty =
                Number(record[`${date}_confirmed_qty`]) > 0 ||
                Number(record[`${date}_projection_qty`]) > 0 ||
                Number(record[`${date}_boking_qty`]) > 0;
              if (!hasQty) return "-";
              return (
                <span
                  style={{
                    cursor: "pointer",
                    color: "#1890ff",
                    display: "block",
                    width: "100%",
                    height: "100%",
                  }}
                  onClick={() => {
                    setNoteModalData({
                      buyer_id: record[`${date}_buyer_id`] || record.buyer_id || null,
                      sub_contract_id: record[`${date}_sub_contract_id`] || record.sub_contract_id || null,
                      year_month: date,
                      notes: value || "",
                      note_for: "monthly",
                      note_id: record[`${date}_note_id`],
                    });
                    setNoteModalVisible(true);
                  }}
                >
                  {value && value.length > 15 ? value.substring(0, 10) + "..." : value || ""}{" "}
                  <EditOutlined style={{ fontSize: 12 }} />
                </span>
              );
            },
          },
        );
      }
      columns.push({
        title: dateLabel,
        onHeaderCell: getHeaderRowStyle(monthColor),
        children: baseChildren,
      });
    });

    // 2. Total Order Qty Columns
    if (reportData.ttl_order_qty) {
      columns.push({
        title: "TTL Order",
        onHeaderCell: getHeaderRowStyle("#fa8c16"), // Orange for totals
        children: [
          {
            title: "Conf. Qty",
            dataIndex: "ttl_order_confirmed_qty",
            key: "ttl_order_confirmed_qty",
            align: "right",
            width: 82,
            render: (value, record) =>
              record?.rowType === "black" || record?.rowType === "others" ? null : formatQty(value),
          },
          {
            title: "Proj. Qty",
            dataIndex: "ttl_order_projection_qty",
            key: "ttl_order_projection_qty",
            align: "right",
            width: 82,
            render: (value, record) =>
              record?.rowType === "black" || record?.rowType === "others" ? null : formatQty(value),
          },
          {
            title: "Book Qty",
            dataIndex: "ttl_order_boking_qty",
            key: "ttl_order_boking_qty",
            align: "right",
            width: 82,
            render: (value, record) =>
              record?.rowType === "black" || record?.rowType === "others" ? null : formatQty(value),
          },
          {
            title: "Total",
            dataIndex: "ttl_order_total",
            key: "ttl_order_total",
            align: "right",
            width: 82,
            render: (value, record) =>
              record?.rowType === "black" || record?.rowType === "others" ? null : formatQty(value),
          },
        ],
      });
    }

    // 3. Sewing Columns
    if (reportData.sewing) {
      columns.push({
        title: "Sewing",
        onHeaderCell: getHeaderRowStyle("#52c41a"), // Soft Green for sewing
        children: [
          {
            title: "Sew Done",
            dataIndex: "sewing_good_garments_qty",
            key: "sewing_good_garments_qty",
            align: "right",
            width: 82,
            render: (value, record) =>
              record?.rowType === "black" || record?.rowType === "others" ? null : formatQty(value),
          },
          {
            title: "Rem. Prod",
            dataIndex: "sewing_remain_prod_qty",
            key: "sewing_remain_prod_qty",
            align: "right",
            width: 82,
            render: (value, record) =>
              record?.rowType === "black" || record?.rowType === "others" ? null : formatQty(value),
          },
        ],
      });
    }

    // 4. Planning Columns
    if (reportData.planning) {
      columns.push({
        title: "Planning",
        onHeaderCell: getHeaderRowStyle("#722ed1"), // Purple for planning
        children: [
          {
            title: "Plan Done",
            dataIndex: "planning_plan_done",
            key: "planning_plan_done",
            align: "right",
            width: 82,
            render: (value, record) =>
              record?.rowType === "black" || record?.rowType === "others" ? null : formatQty(value),
          },
          {
            title: "Bal Plan",
            dataIndex: "planning_bal_to_plan",
            key: "planning_bal_to_plan",
            align: "right",
            width: 82,
            render: (value, record) =>
              record?.rowType === "black" || record?.rowType === "others" ? null : formatQty(value),
          },
          {
            title: "Note",
            dataIndex: "planning_note",
            key: "planning_note",
            width: 90,
            onCell: (record) => {
              if (record?.rowType !== "black" && record?.rowType !== "others" && record?.rowType !== "total") {
                return { style: { backgroundColor: "#fffbe6" } };
              }
              return {};
            },
            render: (value, record) => {
              if (record?.rowType === "black" || record?.rowType === "others" || record?.rowType === "total")
                return value ?? "";
              return (
                <span
                  style={{
                    cursor: "pointer",
                    color: "#1890ff",
                    display: "block",
                    width: "100%",
                    height: "100%",
                  }}
                  onClick={() => {
                    setNoteModalData({
                      buyer_id: record.buyer_id || null,
                      sub_contract_id: record.sub_contract_id || null,
                      year_month: null,
                      notes: value || "",
                      note_for: "planing",
                      note_id: record.planning_note_id,
                    });
                    setNoteModalVisible(true);
                  }}
                >
                  {value && value.length > 10 ? value.substring(0, 10) + "..." : value || ""}{" "}
                  <EditOutlined style={{ fontSize: 12 }} />
                </span>
              );
            },
          },
        ],
      });
    }

    const applyOnCell = (cols) => {
      return cols.map((col) => {
        const ObjectCopy = { ...col };
        if (ObjectCopy.children) {
          ObjectCopy.children = applyOnCell(ObjectCopy.children);
        } else {
          const originalOnCell = ObjectCopy.onCell;
          ObjectCopy.onCell = (record, rowIndex) => {
            let style = {};
            if (originalOnCell) {
              style = { ...style, ...originalOnCell(record, rowIndex)?.style };
            }
            if (record?.rowType === "total") {
              style = { ...style, backgroundColor: "#e6f7ff", fontWeight: 600, borderBottom: "2px solid #1890ff" };
            }
            return { style };
          };
        }
        return ObjectCopy;
      });
    };

    // Helper to pass background color down to children
    const propagateHeaderBgColor = (cols, inheritedBg = undefined) => {
      return cols.map((col) => {
        const inherited = col.onHeaderCell?.().style?.backgroundColor || inheritedBg;
        if (col.children) {
          return { ...col, children: propagateHeaderBgColor(col.children, inherited) };
        }
        if (inherited) {
          col.onHeaderCell = () => ({ style: { backgroundColor: inherited, borderColor: "rgba(255,255,255,0.1)" } });
        }
        return col;
      });
    };

    return applyOnCell(propagateHeaderBgColor(columns));
  };

  const buildDataSource = () => {
    if (!reportData || Object.keys(reportData).length === 0) {
      return [];
    }

    const monthKeys = getMonthKeys();
    const rowKeysSet = new Set();

    monthKeys.forEach((date) => {
      (reportData[date] || []).forEach((item) => {
        if (item.buyer_name) rowKeysSet.add(getRowKey(item));
      });
    });
    const addAggregateRowKeys = (bucket) => {
      if (!bucket) return;
      Object.keys(bucket).forEach((key) => rowKeysSet.add(key));
    };
    addAggregateRowKeys(reportData.ttl_order_qty);
    addAggregateRowKeys(reportData.sewing);
    addAggregateRowKeys(reportData.planning);

    const dataSource = [];
    let keyCounter = 0;

    // 1. Buyer + buying office data rows
    rowKeysSet.forEach((rowKey) => {
      const [buyerName, buyingOfficeName] = rowKey.split("__");
      const row = { key: keyCounter++, rowType: "data", buyer_name: buyerName, buying_office_name: buyingOfficeName };

      let lead = "";
      let mercent = "";
      let country = "";

      monthKeys.forEach((date) => {
        const monthList = reportData[date] || [];
        const buyerData = monthList.find((item) => getRowKey(item) === rowKey);
        if (buyerData) {
          if (!lead && buyerData.lead) lead = buyerData.lead;
          if (!mercent && buyerData.mercent) mercent = buyerData.mercent;
          if (!country && buyerData.country) country = buyerData.country;
          if (!row.buyer_name && buyerData.buyer_name) row.buyer_name = buyerData.buyer_name;
          if (!row.buying_office_name && buyerData.buying_office_name) {
            row.buying_office_name = buyerData.buying_office_name;
          }

          row[`${date}_projection_qty`] = buyerData.projection_qty;
          row[`${date}_confirmed_qty`] = buyerData.confirmed_qty;
          row[`${date}_boking_qty`] = buyerData.boking_qty;
          row[`${date}_boking_date`] = buyerData.boking_date;
          row[`${date}_will_conf_capacity`] = buyerData.will_conf_capacity;
          row[`${date}_note`] = buyerData.note;
          row[`${date}_note_id`] = buyerData.note_id;
          row[`${date}_buyer_id`] = buyerData.buyer_id;
          row[`${date}_sub_contract_id`] = buyerData.sub_contract_factory_id || buyerData.sub_contract_id;
          if (!row.buyer_id && buyerData.buyer_id) row.buyer_id = buyerData.buyer_id;
          if (!row.sub_contract_id && (buyerData.sub_contract_factory_id || buyerData.sub_contract_id)) {
            row.sub_contract_id = buyerData.sub_contract_factory_id || buyerData.sub_contract_id;
          }
          if (buyerData.plan_note !== undefined) {
            row.planning_note = buyerData.plan_note;
            row.planning_note_id = buyerData.plan_note_id;
          }
        } else {
          row[`${date}_projection_qty`] = 0;
          row[`${date}_confirmed_qty`] = 0;
          row[`${date}_boking_qty`] = 0;
          row[`${date}_boking_date`] = "";
          row[`${date}_will_conf_capacity`] = "";
          row[`${date}_note`] = "";
        }
      });

      row.lead = lead;
      row.mercent = mercent;
      row.country = country;

      const ttl = reportData.ttl_order_qty?.[rowKey] || reportData.ttl_order_qty?.[buyerName];
      if (ttl) {
        row.ttl_order_projection_qty = ttl.projection_qty;
        row.ttl_order_confirmed_qty = ttl.confirmed_qty;
        row.ttl_order_boking_qty = ttl.boking_qty;
        row.ttl_order_total = ttl.total;
      }
      const sewing = reportData.sewing?.[rowKey] || reportData.sewing?.[buyerName];
      if (sewing) {
        row.sewing_good_garments_qty = sewing.good_garments_qty;
        row.sewing_remain_prod_qty = sewing.remain_prod_qty;
      }
      const planning = reportData.planning?.[rowKey] || reportData.planning?.[buyerName];
      if (planning) {
        row.planning_plan_done = planning.plan_done;
        row.planning_bal_to_plan = planning.bal_to_plan;
      }

      dataSource.push(row);
    });

    // 2. Total row — sum visible data rows so column totals match the table
    const totalRow = { key: keyCounter++, rowType: "total" };
    totalRow.lead = "Total";
    totalRow.mercent = "";
    totalRow.country = "";
    totalRow.buyer_name = "";
    totalRow.buying_office_name = "";
    monthKeys.forEach((date) => {
      totalRow[`${date}_projection_qty`] = dataSource.reduce((s, r) => s + toNumber(r[`${date}_projection_qty`]), 0);
      totalRow[`${date}_confirmed_qty`] = dataSource.reduce((s, r) => s + toNumber(r[`${date}_confirmed_qty`]), 0);
      totalRow[`${date}_boking_qty`] = dataSource.reduce((s, r) => s + toNumber(r[`${date}_boking_qty`]), 0);
      totalRow[`${date}_boking_date`] = "";
      totalRow[`${date}_will_conf_capacity`] = "";
      totalRow[`${date}_note`] = "";
    });
    totalRow.ttl_order_projection_qty = dataSource.reduce((s, r) => s + toNumber(r.ttl_order_projection_qty), 0);
    totalRow.ttl_order_confirmed_qty = dataSource.reduce((s, r) => s + toNumber(r.ttl_order_confirmed_qty), 0);
    totalRow.ttl_order_boking_qty = dataSource.reduce((s, r) => s + toNumber(r.ttl_order_boking_qty), 0);
    totalRow.ttl_order_total = dataSource.reduce((s, r) => s + toNumber(r.ttl_order_total), 0);
    totalRow.sewing_good_garments_qty = dataSource.reduce((s, r) => s + toNumber(r.sewing_good_garments_qty), 0);
    totalRow.sewing_remain_prod_qty = dataSource.reduce((s, r) => s + toNumber(r.sewing_remain_prod_qty), 0);
    totalRow.planning_plan_done = dataSource.reduce((s, r) => s + toNumber(r.planning_plan_done), 0);
    totalRow.planning_bal_to_plan = dataSource.reduce((s, r) => s + toNumber(r.planning_bal_to_plan), 0);
    dataSource.push(totalRow);

    return dataSource;
  };

  const OTHERS_FIELD_MAP = [
    { key: "sewing_line", label: "Running Line", format: (v) => (v != null ? Number(v).toLocaleString() : "") },
    { key: "working_day", label: "Working Day", format: (v) => (v != null ? Number(v).toLocaleString() : "") },
    {
      key: "man_power",
      label: "Number of machine+ Iron man",
      format: (v) => (v != null ? Number(v).toLocaleString() : ""),
    },
    { key: "average_sam", label: "Average SAM", format: (v) => (v != null ? Number(v).toFixed(2) : "") },
    {
      key: "ten_hour_availableMinutes",
      label: "Available Minute @ 10 Hours",
      format: (v) => (v != null ? Number(v).toLocaleString() : ""),
    },
    {
      key: "eleven_half_hour_availableMinutes",
      label: "Available Minute @ 11.5 Hours",
      format: (v) => (v != null ? Number(v).toLocaleString() : ""),
    },
    {
      key: "plan_produce_minute",
      label: "Plan Produce Minute",
      format: (v) => (v != null ? Number(v).toLocaleString() : ""),
    },
    {
      key: "output_ten_hours",
      label: "Output @ 10 hours (Plan Eff%)",
      format: (v) => (v != null ? Number(v).toLocaleString() : ""),
    },
    {
      key: "output_eleven_half_hours",
      label: "Output @ 11.5 hours (Plan Eff%)",
      format: (v) => (v != null ? Number(v).toLocaleString() : ""),
    },
    { key: "efficiency", label: "Efficiency (Plan)", format: (v) => (v != null ? `${v}%` : "") },
    {
      key: "shortage_minutes_ten_hours",
      label: "Shortage Minutes @ 10 Hours",
      format: (v) => (v != null ? Number(v).toLocaleString() : ""),
    },
    {
      key: "shortage_minutes_eleven_half_hours",
      label: "Shortag Minutes @ 11.5 Hours",
      format: (v) => (v != null ? Number(v).toLocaleString() : ""),
    },
    {
      label: "Shortage QTY @ 10 Hours",
      getValue: (monthData) => {
        const mins = monthData?.shortage_minutes_ten_hours;
        const sam = monthData?.average_sam;
        return mins != null && sam != null && Number(sam) > 0
          ? Math.round(Number(mins) / Number(sam)).toLocaleString()
          : "";
      },
    },
    {
      label: "Shortage QTY @ 11.5 Hours",
      getValue: (monthData) => {
        const mins = monthData?.shortage_minutes_eleven_half_hours;
        const sam = monthData?.average_sam;
        return mins != null && sam != null && Number(sam) > 0
          ? Math.round(Number(mins) / Number(sam)).toLocaleString()
          : "";
      },
    },
  ];

  const getColumnLayout = () => {
    const monthKeys = getMonthKeys();
    if (monthKeys.length === 0) return { monthKeys: [], firstColIndices: [], totalCols: 0 };
    const colsPerMonth = monthKeys.map((_, i) => (i < 3 ? 4 : 6));
    const firstColIndices = [];
    let idx = 5;
    colsPerMonth.forEach((cols) => {
      firstColIndices.push(idx);
      idx += cols;
    });
    const secondColIndices = firstColIndices.map((i) => i + 1);
    const monthColsTotal = colsPerMonth.reduce((a, b) => a + b, 0);
    const ttlStart = 5 + monthColsTotal;
    const sewingStart = ttlStart + 4;
    const planningStart = sewingStart + 2;
    const totalCols = planningStart + 3;
    return { monthKeys, firstColIndices, secondColIndices, totalCols, ttlStart, sewingStart, planningStart };
  };

  const buildSummary = () => {
    const { monthKeys, firstColIndices, secondColIndices, totalCols } = getColumnLayout();
    if (!otherData || Object.keys(otherData).length === 0 || monthKeys.length === 0) return null;

    const renderRow = (label, getValue) => (
      <Table.Summary.Row key={label}>
        {Array.from({ length: totalCols }, (_, colIndex) => {
          const firstMonthIdx = firstColIndices.indexOf(colIndex);
          const secondMonthIdx = secondColIndices.indexOf(colIndex);
          if (firstMonthIdx >= 0) {
            const date = monthKeys[firstMonthIdx];
            const val = getValue(otherData[date], date);
            return (
              <React.Fragment key={colIndex}>
                <Table.Summary.Cell index={colIndex} align="left" style={{ backgroundColor: "#fafafa" }}>
                  <span style={{ fontWeight: 600, color: "#595959" }}>{label}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={colIndex + 1} align="right" style={{ backgroundColor: "#fafafa" }}>
                  <span style={{ fontWeight: 600, color: "#1890ff" }}>{val}</span>
                </Table.Summary.Cell>
              </React.Fragment>
            );
          }
          if (secondMonthIdx >= 0) return null;
          return <Table.Summary.Cell key={colIndex} index={colIndex} style={{ backgroundColor: "#fafafa" }} />;
        })}
      </Table.Summary.Row>
    );

    const emptyRow = (
      <Table.Summary.Row key="empty">
        {Array.from({ length: totalCols }, (_, colIndex) => (
          <Table.Summary.Cell key={colIndex} index={colIndex} style={{ backgroundColor: "#fafafa" }} />
        ))}
      </Table.Summary.Row>
    );

    const getRowGetter = (item) => {
      if (item.getValue) return item.getValue;
      return (monthData) => item.format(monthData?.[item.key]);
    };

    return (
      <Table.Summary>
        {OTHERS_FIELD_MAP.map((item) => renderRow(item.label, getRowGetter(item)))}
        {emptyRow}
      </Table.Summary>
    );
  };

  const handleSaveNote = async () => {
    setNoteModalLoading(true);
    try {
      const body = {
        year_month: noteModalData.year_month,
        notes: noteModalData.notes,
        note_for: noteModalData.note_for,
      };

      if (noteModalData.buyer_id) {
        body.buyer_id = noteModalData.buyer_id;
      } else if (noteModalData.sub_contract_id) {
        body.sub_contract_id = noteModalData.sub_contract_id;
      }

      let response;
      if (noteModalData.note_id) {
        response = await putData(`/api/buyer-monthly-notes/edit/${noteModalData.note_id}`, body);
      } else {
        response = await postData("/api/buyer-monthly-notes/add", body);
      }

      if (response?.code === 200) {
        message.success(response?.message || "Note saved successfully");
        // Re-fetch the report to reflect updated notes
        form.submit();
        setNoteModalVisible(false);
        setNoteModalData({});
      }
    } catch (error) {
      console.error("Error saving note:", error);
      message.error("Failed to save note");
    } finally {
      setNoteModalLoading(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue(getDefaultDateRange());
    form.submit();
  }, [form]);

  return (
    <>
      <AppPageHeader title="Capacity Booking Report" />

      <Collapse defaultActiveKey={["1"]} style={{ marginBottom: 10 }}>
        <Collapse.Panel header={<b>Filter</b>} key="1">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={getDefaultDateRange()}
          >
            <Row gutter={16}>
              <Col span={4}>
                <Form.Item
                  label="Start Date"
                  name="start_date"
                  rules={[{ required: true, message: "Please select start date" }]}
                >
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" placeholder="Select Start Date" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="End Date"
                  name="end_date"
                  rules={[{ required: true, message: "Please select end date" }]}
                >
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" placeholder="Select End Date" />
                </Form.Item>
              </Col>

              <Col span={4}>
                <Form.Item label="Buyer" name="buyer_id">
                  <Select
                    showSearch
                    placeholder="Select Buyer"
                    onSearch={(value) => {
                      fetchBuyers({ name: value });
                    }}
                    optionFilterProp="children"
                    allowClear
                    style={{ width: "100%" }}
                  >
                    {buyers.map((buyer) => (
                      <Select.Option key={buyer.id} value={buyer.id}>
                        {buyer.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Buying Office" name="buying_office_id">
                  <Select
                    showSearch
                    placeholder="Select Buying Office"
                    onSearch={(value) => {
                      fetchBuyingOffices({ name: value });
                    }}
                    onClear={() => fetchBuyingOffices()}
                    optionFilterProp="children"
                    allowClear
                    style={{ width: "100%" }}
                  >
                    {buyingOffices.map((office) => (
                      <Select.Option key={office.id} value={office.id}>
                        {office.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Sub Contract Factory" name="sub_contract_factory_id">
                  <Select
                    showSearch
                    placeholder="Select Sub Contract Factory"
                    onSearch={(value) => {
                      fetchSubContractFactories({ name: value });
                    }}
                    onClear={() => fetchSubContractFactories()}
                    optionFilterProp="children"
                    allowClear
                    style={{ width: "100%" }}
                  >
                    {subContractFactories.map((factory) => (
                      <Select.Option key={factory.id} value={factory.id}>
                        {factory.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Country" name="country_id">
                  <Select
                    showSearch
                    placeholder="Select Country"
                    onSearch={(value) => {
                      fetchCountries({ name: value });
                    }}
                    optionFilterProp="children"
                    allowClear
                    style={{ width: "100%" }}
                  >
                    {countries.map((country) => (
                      <Select.Option key={country.id} value={country.id}>
                        {country.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} justify="end">
              <Col xs={24} sm={6} md={4} lg={3}>
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading} block>
                    Search
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Collapse.Panel>
      </Collapse>

      <Card style={{ width: "100%" }} bodyStyle={{ padding: 0 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>Loading...</div>
        ) : Object.keys(reportData).length > 0 ? (
          (() => {
            const dataSource = buildDataSource();
            return (
              <CompactHorizontalScroll
                className="compact-h-scroll--sticky-table-header"
                contentKey={`${dataSource.length}-${getMonthKeys().join(",")}`}
              >
                <ListTable
                  className="custom-report-table less-padding-table"
                  rowKey="key"
                  columns={buildColumns()}
                  dataSource={dataSource}
                  pagination={false}
                  bordered
                  loading={loading}
                  error={fetchError}
                  onRetry={() => form.submit()}
                  scroll={{ x: "max-content", y: "calc(100vh - 220px)" }}
                  size="small"
                  onRow={(record) => ({
                    style: record?.rowType === "total" ? { backgroundColor: "#e6f7ff", fontWeight: 600 } : undefined,
                  })}
                  summary={buildSummary}
                />
              </CompactHorizontalScroll>
            );
          })()
        ) : (
          <div style={{ textAlign: "center", padding: "50px", color: "#999" }}>
            No data available for the selected date range.
          </div>
        )}
      </Card>

      <Modal
        title={noteModalData.note_for === "planing" ? "Planning Note" : "Monthly Note"}
        open={noteModalVisible}
        onOk={handleSaveNote}
        onCancel={() => {
          setNoteModalVisible(false);
          setNoteModalData({});
        }}
        confirmLoading={noteModalLoading}
        okText="Save"
      >
        <Input.TextArea
          rows={4}
          value={noteModalData.notes}
          onChange={(e) => setNoteModalData((prev) => ({ ...prev, notes: e.target.value }))}
          placeholder="Enter note here..."
        />
      </Modal>
    </>
  );
};

export default PPAnalysisReport;
